"use client";

import { supabase } from "@/lib/supabase";
import type { ToolId, UserSettings } from "@/types/settings";
import { useCallback, useEffect, useRef, useState } from "react";
import { isValidModelId, type ModelId } from "./use-model";
import { resolveVoice, type VoiceId } from "@/lib/voices";
import {
  DEFAULT_USER_SETTINGS,
  profileToolsToRecord,
  recordToProfileTools,
  userSettingsFromProfile,
} from "@/lib/user-settings";

// Defined in @/lib/user-settings so server code can share them.
export { profileToolsToRecord, recordToProfileTools };

// Module-level inflight dedup: when multiple hook instances mount simultaneously
// (e.g. ChatView + ProfilePanel both rendered), they share a single in-flight
// fetch instead of each firing an independent DB query.
type RawProfileRow = { user_settings: unknown; profile_tools: unknown };
const _profileInflight = new Map<string, Promise<RawProfileRow | null>>();

const STORAGE_KEY = "woods-ai:user-settings";

function loadFromStorage(): UserSettings {
  if (typeof window === "undefined") return DEFAULT_USER_SETTINGS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_USER_SETTINGS;
    const r = JSON.parse(stored) as Record<string, unknown>;
    const tools = (r.tools ?? {}) as Record<string, boolean>;
    return {
      model: isValidModelId(r.model) ? r.model : DEFAULT_USER_SETTINGS.model,
      voice: resolveVoice(r.voice),
      tools: {
        webSearch:     tools.webSearch     ?? DEFAULT_USER_SETTINGS.tools.webSearch,
        webFetch:      tools.webFetch      ?? DEFAULT_USER_SETTINGS.tools.webFetch,
        memory:        tools.memory        ?? DEFAULT_USER_SETTINGS.tools.memory,
        codeExecution: tools.codeExecution ?? DEFAULT_USER_SETTINGS.tools.codeExecution,
        knowledgeBase: tools.knowledgeBase ?? DEFAULT_USER_SETTINGS.tools.knowledgeBase,
        myFiles: tools.myFiles ?? DEFAULT_USER_SETTINGS.tools.myFiles,
        searchProjectMeetingMinutes: false,
        searchProjectChats: false,
        projectRegisters: false,
        resolveDateTime:
          tools.resolveDateTime ?? DEFAULT_USER_SETTINGS.tools.resolveDateTime,
        writingStyleProfile:
          tools.writingStyleProfile ?? DEFAULT_USER_SETTINGS.tools.writingStyleProfile,
        // woodsIdentity is always on — not user-controllable
        woodsIdentity: true,
      },
      preferences: typeof r.preferences === "string" ? r.preferences : DEFAULT_USER_SETTINGS.preferences,
      resolveDateTimeConfig: {
        defaultTimezone:
          typeof r.resolveDateTimeConfig === "object" &&
          r.resolveDateTimeConfig !== null &&
          typeof (r.resolveDateTimeConfig as { defaultTimezone?: unknown }).defaultTimezone === "string" &&
          (r.resolveDateTimeConfig as { defaultTimezone: string }).defaultTimezone.trim().length > 0
            ? (r.resolveDateTimeConfig as { defaultTimezone: string }).defaultTimezone
            : DEFAULT_USER_SETTINGS.resolveDateTimeConfig.defaultTimezone,
      },
    };
  } catch {
    return DEFAULT_USER_SETTINGS;
  }
}

function persistLocal(settings: UserSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export const useUserSettings = (userId?: string | null) => {
  const [settings, setSettings] = useState<UserSettings>(loadFromStorage);
  // True once the authoritative DB profile_tools have been loaded.
  // setTool must not write back to the DB until then to avoid the race where
  // stale localStorage state overwrites the authoritative DB value before the
  // fetch completes.
  const dbLoadedRef = useRef(false);

  useEffect(() => {
    if (!userId) return;
    let active = true;

    const existing = _profileInflight.get(userId);
    const promise: Promise<RawProfileRow | null> = existing ?? (() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const p: Promise<RawProfileRow | null> = (supabase.from("profiles") as any)
        .select("user_settings, profile_tools")
        .eq("user_id", userId)
        .single()
        .then(({ data }: { data: RawProfileRow | null }) => data)
        .finally(() => _profileInflight.delete(userId));
      _profileInflight.set(userId, p);
      return p;
    })();

    promise.then((data: RawProfileRow | null) => {
      if (!active || !data) return;
      const next: UserSettings = userSettingsFromProfile(data);
      dbLoadedRef.current = true;
      setSettings(next);
      persistLocal(next);
    });

    return () => { active = false; };
  }, [userId]);

  const save = useCallback(
    (next: UserSettings) => {
      persistLocal(next);
      if (userId) {
        supabase
          .from("profiles")
          .update({
            user_settings: {
              model: next.model,
              voice: next.voice,
              preferences: next.preferences,
              resolveDateTimeConfig: next.resolveDateTimeConfig,
            },
          })
          .eq("user_id", userId)
          .then(({ error }) => {
            if (error) console.error("[use-user-settings] save error:", error.message);
          });
      }
    },
    [userId]
  );

  const setModel = useCallback(
    (model: ModelId) => {
      setSettings((prev) => {
        const next = { ...prev, model };
        persistLocal(next);
        if (userId) {
          supabase
            .from("profiles")
            .update({
              user_settings: {
                model,
                voice: next.voice,
                preferences: next.preferences,
                resolveDateTimeConfig: next.resolveDateTimeConfig,
              },
            })
            .eq("user_id", userId)
            .then(() => {});
        }
        return next;
      });
    },
    [userId],
  );

  const setTool = useCallback(
    (tool: ToolId, enabled: boolean) => {
      setSettings((prev) => {
        const next = { ...prev, tools: { ...prev.tools, [tool]: enabled } };
        persistLocal(next);
        // Only persist to DB after the authoritative DB state has loaded to
        // prevent a race where stale localStorage values overwrite the DB.
        if (userId && dbLoadedRef.current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase.from("profiles") as any)
            .update({ profile_tools: recordToProfileTools(next.tools) })
            .eq("user_id", userId)
            .then(() => {});
        }
        return next;
      });
    },
    [userId],
  );

  const setPreferences = useCallback(
    (preferences: string) => {
      setSettings((prev) => {
        const next = { ...prev, preferences };
        save(next);
        return next;
      });
    },
    [save]
  );

  const setVoice = useCallback(
    (voice: VoiceId) => {
      setSettings((prev) => {
        const next = { ...prev, voice: resolveVoice(voice) };
        save(next);
        return next;
      });
    },
    [save],
  );

  const setResolveDateTimeDefaultTimezone = useCallback(
    (defaultTimezone: string) => {
      setSettings((prev) => {
        const next = {
          ...prev,
          resolveDateTimeConfig: {
            defaultTimezone,
          },
        };
        save(next);
        return next;
      });
    },
    [save],
  );

  return { settings, setModel, setVoice, setTool, setPreferences, setResolveDateTimeDefaultTimezone };
};
